import { css } from '@emotion/react';
import {
  faShare, faUsersMedical,
} from '@fortawesome/pro-solid-svg-icons';
import { type FC } from 'react';
import { useDispatch } from 'react-redux';
import { Accordion } from '~/_shared/baseComponents/accordion';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { useOnUnmount } from '~/_shared/hooks/useOnUnmount';
import { useTranslation } from '~/_shared/utils/hooks';
import { MapNameSectionComponent } from '~/map/basicInfo/mapNameSection';
import { isInfoOfBaseLayer } from '~/map/listing/mapListing.helpers';
import { getMapOwnerUserId } from '~/map/map.helpers';
import { ShareMapSelectItemComponent } from '~/map/share/select/shareMapSelectItem.component';
import {
  type ModalProps, ModalType,
} from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { closeLeftSidebar } from '~/store/frontendState/leftSidebar/leftSidebar.actionCreators';
import {
  useMapInfoDataSelector, useMapInfoPrivacySelector,
} from '~/store/mapInfo/mapInfo.selectors';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { useUserDataSelector } from '~/store/userData/userData.selectors';
import {
  s, Trans,
} from '~/translations/Trans';

const modalContentStyle = css({
  padding: 0,
});

const accordionPanelStyle = css({
  padding: 20,
});

const accordionHeadingStyle = css({
  textTransform: 'uppercase',
});

const headingSubStyle = css({
  fontSize: '12px',
});

const shareWrapperStyle = css({
  display: 'flex',
  gap: 10,
  flexDirection: 'column',
});

export type ShareMapSelectProps = ModalProps;

export const ShareMapSelectComponent: FC<ShareMapSelectProps> = (props) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const mapId = useMapIdSelector();
  const mapInfo = useMapInfoDataSelector();
  const mapPrivacyInfo = useMapInfoPrivacySelector();
  const currentUserId = useUserDataSelector().id;

  const { replaceCurrentModal: openShareMapModal } = useModal(ModalType.ShareMap);
  const { replaceCurrentModal: openTeamShareMapModal } = useModal(ModalType.TeamShareMap);

  useOnUnmount(() => dispatch(closeLeftSidebar()));

  if (!mapInfo || !mapId) {
    return null;
  }

  const mapName = mapInfo.name;
  const isSnapshot = !!mapInfo.parentMap && !isInfoOfBaseLayer(mapInfo);

  const isCurrentUserOwner = mapInfo && getMapOwnerUserId(mapInfo) === currentUserId;

  const commonProps = {
    isSnapshot,
    mapId,
    mapName,
  };

  const onPresentationalShareClick = () => {
    openShareMapModal({
      ...commonProps,
      parentMapPrivacy: mapPrivacyInfo?.parentMapPrivacy || null,
      mapSource: mapInfo?.source,
    });
  };
  const onTeamShareClick = isCurrentUserOwner ? () => {
    openTeamShareMapModal(commonProps);
  } : undefined;

  const teamShareError = isCurrentUserOwner ? undefined : t('teamShareMap.onlyOwnerCanShare');

  return (
    <ModalComponent
      caption={t('Share Your Map')}
      contentStyle={modalContentStyle}
      isOpen={props.isOpen}
      onClose={props.onClose}
      maxWidth={590}
    >
      <MapNameSectionComponent
        isSnapshot={isSnapshot}
        name={mapName}
      />

      <Accordion>
        <Accordion.Item
          isLocked
          open
        >
          <Accordion.ItemHeading css={accordionHeadingStyle}>
            {t('Choose audience')}
          </Accordion.ItemHeading>
          <Accordion.Panel css={accordionPanelStyle}>
            <div css={shareWrapperStyle}>
              <ShareMapSelectItemComponent
                icon={faUsersMedical}
                heading={t('Share with your team')}
                description={t('Assign specific map roles to your team members.')}
                onClick={onTeamShareClick}
                error={teamShareError}
              />

              <ShareMapSelectItemComponent
                icon={faShare}
                heading={(
                  <Trans i18nKey="Share externally <1>(Public map link, password protected, etc)</1>">
                    {s('Share externally ')}
                    <span css={headingSubStyle}>{s('(Public map link, password protected, etc)')}</span>
                  </Trans>
                )}
                description={t('Make your map publicly available for view. You will be able to add password protection.')}
                onClick={onPresentationalShareClick}
              />
            </div>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </ModalComponent>
  );
};
